/**
 * B526 (g3) — what Viator itself calls, in its Reservation System API v2
 * shapes (Viator's OpenAPI, read 2026-10-01; docs/ota/PARTNER-CONNECTIVITY.md
 * § Viator). Like the OCTO doors (octo.ts), every op admits the RESELLER
 * principal an `ota1.` key exchanges for; the handlers also refuse a partner
 * whose dialect is not VIATOR. The partner and its channel are the
 * principal's — no field names either. `supplierId` (Viator's id for the
 * operator) is carried because Viator sends it; the key decides who calls.
 */
import { z } from "zod";
import { readOp, writeOp, zId } from "./common.js";

const zInt = z.number().int().safe();
const zDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const zHhmm = z.string().regex(/^\d{2}:\d{2}$/);
const zTravelerType = z.enum(["ADULT", "SENIOR", "YOUTH", "CHILD", "INFANT"]);
/** Viator's ticket type as asked — the translator judges it (UNIT and unknown types are unsupported, not malformed). */
const zTicket = z.object({ type: z.string().min(1).max(20), quantity: zInt }).strict();

const zCapacity = z
  .object({
    type: z.literal("LIMITED"),
    vacancies: z.array(z.object({ types: z.array(zTravelerType), quantity: zInt, quantityType: z.literal("SHARED") }).strict()),
    original: zInt,
    remaining: zInt,
  })
  .strict();

const zPrice = z
  .object({
    type: z.literal("PER_PERSON_PRICE"),
    prices: z.array(z.object({ types: z.array(zTravelerType), retailPrice: z.number(), netPrice: z.number() }).strict()),
  })
  .strict();

const zEvent = z.union([
  z.object({ status: z.literal("AVAILABLE"), startTime: zHhmm, bookingCutoff: z.string(), capacity: zCapacity, price: zPrice }).strict(),
  z
    .object({
      status: z.literal("UNAVAILABLE"),
      unavailableReason: z.enum(["SOLD_OUT", "PAST_BOOKING_CUTOFF", "UNSUPPORTED_TICKET_COMBINATION", "LIMITED_AVAILABILITY"]),
      startTime: zHhmm,
      bookingCutoff: z.string(),
      capacity: zCapacity,
    })
    .strict(),
]);

const EXAMPLE_EVENT = {
  status: "AVAILABLE" as const,
  startTime: "09:00",
  bookingCutoff: "2026-10-02T15:00:00Z",
  capacity: { type: "LIMITED" as const, vacancies: [{ types: ["ADULT" as const], quantity: 40, quantityType: "SHARED" as const }], original: 60, remaining: 40 },
  price: { type: "PER_PERSON_PRICE" as const, prices: [{ types: ["ADULT" as const], retailPrice: 30, netPrice: 24 }] },
};

export const viatorAvailabilityCheck = readOp({
  name: "viator.availability.check",
  permission: "supply:read",
  principals: ["RESELLER"],
  fields: {
    supplierId: zInt.optional(),
    travelDate: zDay,
    productOptions: z.array(z.object({ productOptionId: zId, startTimes: z.array(zHhmm).max(48).optional() }).strict()).min(1).max(50),
    tickets: z.array(zTicket).min(1).max(10),
    totalTravelers: zInt,
  },
  response: z.object({ productOptions: z.array(z.object({ productOptionId: zId, currency: z.string(), events: z.array(zEvent) }).strict()) }).strict(),
  exampleRequest: { supplierId: 1234, travelDate: "2026-10-02", productOptions: [{ productOptionId: "harbor-am" }], tickets: [{ type: "ADULT", quantity: 2 }], totalTravelers: 2 },
  exampleResponse: { productOptions: [{ productOptionId: "harbor-am", currency: "USD", events: [EXAMPLE_EVENT] }] },
});

export const viatorAvailabilityCalendar = readOp({
  name: "viator.availability.calendar",
  permission: "supply:read",
  principals: ["RESELLER"],
  fields: { supplierId: zInt.optional(), productOptionIds: z.array(zId).min(1).max(50), startDate: zDay, endDate: zDay },
  response: z
    .object({
      productOptions: z.array(
        z.object({ productOptionId: zId, currency: z.string(), dates: z.array(z.object({ travelDate: zDay, events: z.array(zEvent) }).strict()) }).strict(),
      ),
    })
    .strict(),
  exampleRequest: { supplierId: 1234, productOptionIds: ["harbor-am"], startDate: "2026-10-02", endDate: "2026-10-03" },
  exampleResponse: { productOptions: [{ productOptionId: "harbor-am", currency: "USD", dates: [{ travelDate: "2026-10-02", events: [EXAMPLE_EVENT] }] }] },
});

export const viatorReserve = writeOp({
  name: "viator.reserve",
  permission: "order:write",
  principals: ["RESELLER"],
  fields: {
    supplierId: zInt.optional(),
    productOptionId: zId,
    travelDate: zDay,
    startTime: zHhmm.optional(),
    tickets: z.array(zTicket).min(1).max(10),
    totalTravelers: zInt,
  },
  response: z.union([
    z.object({ status: z.literal("RESERVED"), expiration: z.string(), reference: zId, currency: z.string(), price: zPrice }).strict(),
    z
      .object({
        status: z.literal("NOT_RESERVED"),
        reason: z.enum(["SOLD_OUT", "PAST_BOOKING_CUTOFF", "UNSUPPORTED_TICKET_COMBINATION", "LIMITED_AVAILABILITY", "NO_EVENT"]),
      })
      .strict(),
  ]),
  exampleRequest: { supplierId: 1234, productOptionId: "harbor-am", travelDate: "2026-10-02", startTime: "09:00", tickets: [{ type: "ADULT", quantity: 2 }], totalTravelers: 2 },
  exampleResponse: { status: "RESERVED" as const, expiration: "2026-10-01T12:15:00Z", reference: "b-0001", currency: "USD", price: EXAMPLE_EVENT.price },
});
